import { bookingsService } from '@/api/bookings.service';
import type { QueryLocale } from '@/entities/auth/local.enum';
import type {
  Booking,
  CreateBookingDto,
  FindAllBookingsQueryDto,
  FindOneBookingQueryDto,
  UpdateBookingDto,
  UpdateBookingQueryDto,
} from '@/entities/bookings/booking.entity';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';
import type {
  CreateProcessSessionDto,
  PatchProcessSessionDto,
  ProcessSessionResponseDto,
} from '@carvia/ros-client-types';
import type {
  BookingsControllerGetCarSwapProtocolTemplateData,
  CompleteHandoverDto,
  ManualLaneCreditDto,
  WriteOffAmountDto,
} from '@carvia/ros-client-types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { QueryClient } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toRef, toValue } from 'vue';
import { ADDITIONAL_COSTS_QUERY_KEY } from './use-pricing';
import { INVOICES_QUERY_KEY } from './use-invoices';
import { LEDGER_ENTRIES_QUERY_KEY, NETTING_ELIGIBILITY_QUERY_KEY } from './use-ledger';
import { PAYMENTS_QUERY_KEY } from './use-payments';

export const BOOKINGS_QUERY_KEY = 'bookings';

export const useBooking = (
  id: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
  query?: MaybeRefOrGetter<FindOneBookingQueryDto | undefined>,
) => {
  return useQuery({
    queryKey: computed(() => {
      const bookingId = toValue(id);
      const bookingQuery = toValue(query);
      if (bookingQuery?.handoverAdjustment) {
        return [BOOKINGS_QUERY_KEY, bookingId, { handoverAdjustment: true }];
      }
      return [BOOKINGS_QUERY_KEY, bookingId];
    }),
    queryFn: async () => {
      const bookingId = toValue(id)!;
      const bookingQuery = toValue(query);
      return bookingQuery
        ? bookingsService.getOneWithQuery(bookingId, bookingQuery)
        : bookingsService.getOne(bookingId);
    },
    enabled: computed(() => toValue(enabled) && !!toValue(id)),
  });
};

export const useBookingsWithQuery = (
  query: MaybeRefOrGetter<FindAllBookingsQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [BOOKINGS_QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      bookingsService.getAllWithQuery({
        ...toValue(query),
        page: pageParam,
      }),
    enabled: toRef(enabled),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
  });
};

export const useCountBookingsOfUser = (userId: MaybeRefOrGetter<string>) => {
  return useQuery({
    queryKey: [BOOKINGS_QUERY_KEY, 'count-bookings-of-user', toRef(userId)],
    queryFn: () => bookingsService.countBookingsOfUser(toValue(userId)),
  });
};

export const useBookingsActionNeededCounts = (
  stationId: MaybeRefOrGetter<string | undefined> = undefined,
  query: MaybeRefOrGetter<string | undefined> = undefined,
  bookingNumber: MaybeRefOrGetter<number | undefined> = undefined,
) => {
  return useQuery({
    queryKey: [BOOKINGS_QUERY_KEY, 'action-needed-counts', stationId, query, bookingNumber],
    queryFn: () =>
      bookingsService.getActionNeededCounts({
        stationId: toValue(stationId),
        query: toValue(query),
        bookingNumber: toValue(bookingNumber),
      }),
  });
};

export const useUpdateBooking = (options?: { silent?: boolean }) => {
  const queryClient = useQueryClient();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationKey: [BOOKINGS_QUERY_KEY],
    mutationFn: ({
      id,
      booking,
      query,
    }: {
      id: string;
      booking: UpdateBookingDto;
      query?: UpdateBookingQueryDto;
    }) => bookingsService.update(id, booking, query),
    onMutate: silent
      ? undefined
      : () => ({
          successMessage: 'Booking updated',
          invalidateQueryKeys: null,
        }),
    onSuccess: (result, variables, onMutateResult, context) => {
      if (variables.booking.dryRun) return;
      if (!silent) {
        queryClient
          .getMutationDefaults([])
          ?.onSuccess?.(result, variables, onMutateResult, context);
      }
      queryClient.invalidateQueries({ queryKey: [ADDITIONAL_COSTS_QUERY_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [NETTING_ELIGIBILITY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });

      const handoverQueryKey = [
        BOOKINGS_QUERY_KEY,
        variables.id,
        { handoverAdjustment: true },
      ] as const;
      const previousHandoverBooking = variables.query?.handoverAdjustment
        ? undefined
        : queryClient.getQueryData<Booking>(handoverQueryKey);

      updateItemOnQueriesData<Booking>([BOOKINGS_QUERY_KEY], result, queryClient);

      // Non-handover responses hydrate availableExtras at list prices — keep surcharged
      // extras on the handover overview cache entry.
      if (previousHandoverBooking?.priceCalculation?.availableExtras) {
        queryClient.setQueryData<Booking>(handoverQueryKey, {
          ...result,
          priceCalculation: {
            ...result.priceCalculation,
            availableExtras: previousHandoverBooking.priceCalculation.availableExtras,
          },
        });
      }
    },
  });
};

export const useCreateBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [BOOKINGS_QUERY_KEY],
    mutationFn: (booking: CreateBookingDto) => bookingsService.create(booking),
    onMutate: () => ({
      successMessage: 'Booking created successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      addItemToQueriesData<Booking>([BOOKINGS_QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [BOOKINGS_QUERY_KEY],
    mutationFn: (removeId: string) => bookingsService.remove(removeId),
    onMutate: () => ({
      successMessage: 'Booking deleted successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<Booking>([BOOKINGS_QUERY_KEY], variables, queryClient);
    },
  });
};

export const useBookingContractTemplate = (
  id: MaybeRefOrGetter<string>,
  lang: MaybeRefOrGetter<QueryLocale>,
) => {
  return useQuery({
    queryKey: ['contractTemplate', id, lang],
    queryFn: () => bookingsService.getBookingContractTemplate(toValue(id), toValue(lang)),
    // A language switch is a new key. Keep the previous HTML so isLoading stays
    // false and the signature canvas is not unmounted mid-handover.
    placeholderData: (previousHtml) => previousHtml,
  });
};

export const useCarSwapProtocolTemplate = (
  id: MaybeRefOrGetter<string>,
  query: MaybeRefOrGetter<
    BookingsControllerGetCarSwapProtocolTemplateData['query'] & { lang: QueryLocale }
  >,
) => {
  return useQuery({
    queryKey: ['carSwapProtocolTemplate', id, query],
    queryFn: () => bookingsService.getCarSwapProtocolTemplate(toValue(id), toValue(query)),
  });
};

export const useCompleteHandover = () => {
  return useMutation({
    mutationKey: ['completeHandover'],
    mutationFn: ({ bookingId, data }: { bookingId: string; data: CompleteHandoverDto }) =>
      bookingsService.completeHandover(bookingId, data),
    onMutate: () => ({ successMessage: 'Handover completed successfully' }),
  });
};

export const useEnsureBookingCustomerLinked = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['ensureBookingCustomerLinked'],
    mutationFn: (bookingId: string) => bookingsService.ensureCustomerLinked(bookingId),
    onSuccess: (booking) => {
      queryClient.setQueryData([BOOKINGS_QUERY_KEY, booking.id], booking);
    },
  });
};

export const useResendBookingContract = () => {
  return useMutation({
    mutationKey: ['resend-contract'],
    mutationFn: (bookingId: string) => bookingsService.resendContract(bookingId),
    onMutate: () => ({ successMessage: 'Contract sent successfully' }),
  });
};

export const useResendUpdatedBookingContractImmediately = () => {
  return useMutation({
    mutationKey: ['resend-updated-contract-immediately'],
    mutationFn: (bookingId: string) => bookingsService.resendUpdatedContractImmediately(bookingId),
    onMutate: () => ({ successMessage: 'Updated contract sent successfully' }),
  });
};

export const useResendBookingConfirmation = () => {
  return useMutation({
    mutationKey: ['resend-confirmation-email'],
    mutationFn: (bookingId: string) => bookingsService.resendBookingConfirmation(bookingId),
    onMutate: () => ({ successMessage: 'Confirmation sent successfully' }),
  });
};

export const useSendRentalKeyLink = () => {
  return useMutation({
    mutationKey: ['send-rental-key-link'],
    mutationFn: ({ bookingId, sendMail }: { bookingId: string; sendMail: boolean }) =>
      bookingsService.sendRentalKeyLink(bookingId, sendMail),
    onMutate: ({ sendMail }) =>
      sendMail ? { successMessage: 'Rental key link sent successfully' } : {},
  });
};

export const useSendDepositReceipt = () => {
  return useMutation({
    mutationKey: ['send-deposit-receipt'],
    mutationFn: ({ bookingId, receiptText }: { bookingId: string; receiptText: string }) =>
      bookingsService.sendDepositReceipt(bookingId, receiptText),
    onMutate: () => ({ successMessage: 'Deposit receipt sent successfully' }),
  });
};

export const useConvertToLongTerm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [BOOKINGS_QUERY_KEY, 'convert-to-long-term'],
    mutationFn: ({
      id,
      dto,
    }: {
      id: string;
      dto: {
        bookedExtras: { vehicleExtraId?: string; vehicleExtraOptionId?: string }[];
        customBookedExtras?: unknown;
        customTotalBasePrice: number;
        quoteStartDate: string;
        invoicePickupTransfer?: boolean;
        invoiceDropoffTransfer?: boolean;
      };
    }) => bookingsService.convertToLongTerm(id, dto),
    onMutate: () => ({
      successMessage: 'Booking converted to long-term',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      updateItemOnQueriesData<Booking>([BOOKINGS_QUERY_KEY], result, queryClient);
    },
  });
};

export const useRefreshBookingConfirmation = () => {
  return useMutation({
    mutationKey: ['refresh-booking-confirmation'],
    mutationFn: (bookingId: string) => bookingsService.refreshBookingConfirmation(bookingId),
  });
};

export const useSuggestCustomerData = (q: MaybeRefOrGetter<string>) => {
  const enabled = computed(() => !!toValue(q)?.trim());
  return useQuery({
    queryKey: ['suggest-customer-data', toRef(q)],
    queryFn: async () => bookingsService.suggestCustomerData(toValue(q)),
    enabled,
  });
};

export const useProcessSession = (
  bookingId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: ['process-session', toRef(bookingId)],
    queryFn: () => bookingsService.getProcessSession(toValue(bookingId)!),
    enabled: computed(() => toValue(enabled) && !!toValue(bookingId)),
  });
};

export const useCreateProcessSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['process-session', 'create'],
    mutationFn: ({ bookingId, dto }: { bookingId: string; dto: CreateProcessSessionDto }) =>
      bookingsService.createProcessSession(bookingId, dto),
    onSuccess: (result, variables) => {
      queryClient.setQueryData(['process-session', variables.bookingId], result);
    },
  });
};

export const PROCESS_SESSION_PATCH_MUTATION_KEY = ['process-session', 'patch'] as const;

export function hasPendingProcessSessionPatch(
  queryClient: QueryClient,
  bookingId: string,
): boolean {
  return queryClient
    .getMutationCache()
    .findAll({ mutationKey: [...PROCESS_SESSION_PATCH_MUTATION_KEY], status: 'pending' })
    .some((mutation) => {
      const variables = mutation.state.variables as { bookingId?: string } | undefined;
      return variables?.bookingId === bookingId;
    });
}

export const usePatchProcessSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: PROCESS_SESSION_PATCH_MUTATION_KEY,
    mutationFn: ({ bookingId, dto }: { bookingId: string; dto: PatchProcessSessionDto }) =>
      bookingsService.patchProcessSession(bookingId, dto),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<ProcessSessionResponseDto>(
        ['process-session', variables.bookingId],
        (current) => (!current || result.revision >= current.revision ? result : current),
      );
    },
    onError: (_error, { bookingId }) => {
      void queryClient.invalidateQueries({ queryKey: ['process-session', bookingId] });
    },
  });
};

export const useDeleteProcessSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['process-session', 'delete'],
    mutationFn: (bookingId: string) => bookingsService.deleteProcessSession(bookingId),
    onSuccess: (_result, bookingId) => {
      queryClient.setQueryData(['process-session', bookingId], null);
    },
  });
};

export const useWriteOffBookingAmount = (bookingId: string) => {
  return useMutation({
    mutationKey: ['write-off-booking-amount'],
    mutationFn: (dto: WriteOffAmountDto) => bookingsService.writeOffAmount(bookingId, dto),
    onMutate: () => ({
      invalidateQueryKeys: [BOOKINGS_QUERY_KEY, bookingId],
    }),
  });
};

export const useManualLaneCredit = (bookingId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['manual-lane-credit'],
    mutationFn: (dto: ManualLaneCreditDto) => bookingsService.manualLaneCredit(bookingId, dto),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY, bookingId] });
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
    },
  });
};
